import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient, apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { UserPlus, Shield, UserX, RefreshCw, Key, Eye, EyeOff, MoreVertical, UserCheck, Edit2, User as UserIcon } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ProfilePictureUploadDialog } from '@/components/profile-picture-upload-dialog';
import { getUserColor } from '@/lib/user-color';

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'admin' | 'team' | 'external' | 'guest';
  isActive: boolean;
  lockedUntil?: string | null;
  failedAttempts: number;
  createdAt: string;
  lastLogin?: string;
  shortCode?: string | null;
  colorCode?: string | null;
  profileImage?: string | null;
}

export function UserManagementSettings() {
  const { toast } = useToast();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showProfilePictureDialog, setShowProfilePictureDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Form states for creating new user
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserUsername, setNewUserUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'team' | 'external' | 'guest'>('team');
  const [showPassword, setShowPassword] = useState(false);
  
  // Edit user form
  const [editEmail, setEditEmail] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editShortCode, setEditShortCode] = useState('');
  const [editColorCode, setEditColorCode] = useState('');
  
  // Password reset form
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Fetch all users
  const { data: users = [], isLoading } = useQuery<User[]>({
    queryKey: ['/api/auth/users'],
    retry: 1,
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest('POST', '/api/auth/register', {
        email: newUserEmail,
        username: newUserUsername,
        password: newUserPassword,
        firstName: newUserFirstName,
        lastName: newUserLastName,
        role: newUserRole,
      });
    },
    onSuccess: () => {
      toast({
        title: 'User created',
        description: `Successfully created user ${newUserEmail}`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/users'] });
      setShowCreateDialog(false);
      resetCreateForm();
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to create user',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update user role mutation
  const updateRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      return await apiRequest('PATCH', `/api/auth/users/${userId}/role`, { role });
    },
    onSuccess: (_, { userId }) => {
      toast({
        title: 'Role updated',
        description: 'User role has been updated successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/users'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update role',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update user status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ userId, isActive }: { userId: string; isActive: boolean }) => {
      return await apiRequest('PATCH', `/api/auth/users/${userId}/status`, { isActive });
    },
    onSuccess: (_, { isActive }) => {
      toast({
        title: isActive ? 'User activated' : 'User deactivated',
        description: `User has been ${isActive ? 'activated' : 'deactivated'} successfully`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/users'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update status',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Unlock user mutation
  const unlockUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return await apiRequest('POST', `/api/auth/users/${userId}/unlock`);
    },
    onSuccess: () => {
      toast({
        title: 'User unlocked',
        description: 'User account has been unlocked successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/users'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to unlock user',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async ({ userId, password }: { userId: string; password: string }) => {
      return await apiRequest('PATCH', `/api/auth/users/${userId}/password`, { password });
    },
    onSuccess: () => {
      toast({
        title: 'Password reset',
        description: 'User password has been reset successfully',
      });
      setShowPasswordDialog(false);
      setSelectedUser(null);
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to reset password',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Edit user mutation
  const editUserMutation = useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: any }) => {
      return await apiRequest('PATCH', `/api/auth/users/${userId}`, data);
    },
    onSuccess: () => {
      toast({
        title: 'User updated',
        description: 'User information has been updated successfully',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/auth/users'] });
      queryClient.invalidateQueries({ queryKey: ['/api/artists'] });
      setShowEditDialog(false);
      setSelectedUser(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update user',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const resetCreateForm = () => {
    setNewUserEmail('');
    setNewUserUsername('');
    setNewUserPassword('');
    setNewUserFirstName('');
    setNewUserLastName('');
    setNewUserRole('team');
    setShowPassword(false);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-600';
      case 'team':
        return 'bg-blue-600';
      case 'external':
        return 'bg-green-600';
      case 'guest':
        return 'bg-gray-600';
      default:
        return 'bg-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header with Add User button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-white">User Management</h3>
          <p className="text-sm text-gray-400 mt-1">Manage system users and their access levels</p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Users List */}
      <div className="border border-gray-600 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700">
            <tr>
              <th className="text-left p-3 text-white font-medium">User</th>
              <th className="text-left p-3 text-white font-medium">Profile Picture</th>
              <th className="text-left p-3 text-white font-medium">Short Code</th>
              <th className="text-left p-3 text-white font-medium">Role</th>
              <th className="text-left p-3 text-white font-medium">Status</th>
              <th className="text-left p-3 text-white font-medium">Last Login</th>
              <th className="text-left p-3 text-white font-medium">Created</th>
              <th className="text-right p-3 text-white font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center p-6 text-gray-400">
                  Loading users...
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center p-6 text-gray-400">
                  No users found
                </td>
              </tr>
            ) : (
              users.map((user: User) => (
                <tr key={user.id} className="border-t border-gray-700 hover:bg-gray-800">
                  <td className="p-3">
                    <div>
                      <div className="text-white font-medium">{user.email}</div>
                      {(user.firstName || user.lastName) && (
                        <div className="text-sm text-gray-400">
                          {[user.firstName, user.lastName].filter(Boolean).join(' ')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => {
                        setSelectedUser(user);
                        setShowProfilePictureDialog(true);
                      }}
                      className="relative group cursor-pointer"
                      data-testid={user.profileImage ? `profile-picture-${user.id}` : `add-profile-picture-${user.id}`}
                    >
                      {user.profileImage ? (
                        <img
                          src={user.profileImage}
                          alt={`${user.email} profile`}
                          className="w-10 h-10 rounded-full object-cover border-2 border-gray-600 group-hover:border-blue-500 transition-colors"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-700 border-2 border-gray-600 group-hover:border-blue-500 transition-colors flex items-center justify-center">
                          <UserIcon className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                        </div>
                      )}
                    </button>
                  </td>
                  <td className="p-3">
                    {user.shortCode ? (
                      <div 
                        className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
                        style={{ backgroundColor: getUserColor(user.colorCode) }}
                      >
                        {user.shortCode}
                      </div>
                    ) : (
                      <span className="text-gray-500 text-sm">-</span>
                    )}
                  </td>
                  <td className="p-3">
                    <Badge className={`${getRoleBadgeColor(user.role)} text-white`}>
                      {user.role.toUpperCase()}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {user.isActive ? (
                        <Badge className="bg-green-600 text-white">
                          <UserCheck className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-600 text-white">
                          <UserX className="w-3 h-3 mr-1" />
                          Inactive
                        </Badge>
                      )}
                      {user.lockedUntil && new Date(user.lockedUntil) > new Date() && (
                        <Badge className="bg-red-600 text-white">
                          Locked
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-gray-400 text-sm">
                    {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                  </td>
                  <td className="p-3 text-gray-400 text-sm">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="p-3">
                    <div className="flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-gray-800 border-gray-700">
                          <DropdownMenuLabel className="text-gray-300">Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator className="bg-gray-700" />
                          
                          {/* Edit User */}
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(user);
                              setEditEmail(user.email);
                              setEditFirstName(user.firstName || '');
                              setEditLastName(user.lastName || '');
                              setEditShortCode(user.shortCode || '');
                              setEditColorCode(user.colorCode || '');
                              setShowEditDialog(true);
                            }}
                            className="text-gray-300 hover:bg-gray-700 cursor-pointer"
                          >
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          
                          {/* Change Role */}
                          <DropdownMenuItem className="text-gray-300 hover:bg-gray-700">
                            <Shield className="w-4 h-4 mr-2" />
                            <Select
                              value={user.role}
                              onValueChange={(value) => updateRoleMutation.mutate({ userId: user.id, role: value })}
                            >
                              <SelectTrigger className="h-6 bg-transparent border-0 text-gray-300">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-700">
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="team">Team</SelectItem>
                                <SelectItem value="external">External</SelectItem>
                                <SelectItem value="guest">Guest</SelectItem>
                              </SelectContent>
                            </Select>
                          </DropdownMenuItem>
                          
                          {/* Toggle Active Status */}
                          <DropdownMenuItem
                            onClick={() => updateStatusMutation.mutate({ userId: user.id, isActive: !user.isActive })}
                            className="text-gray-300 hover:bg-gray-700 cursor-pointer"
                          >
                            <UserCheck className="w-4 h-4 mr-2" />
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          
                          {/* Unlock User */}
                          {user.lockedUntil && new Date(user.lockedUntil) > new Date() && (
                            <DropdownMenuItem
                              onClick={() => unlockUserMutation.mutate(user.id)}
                              className="text-gray-300 hover:bg-gray-700 cursor-pointer"
                            >
                              <RefreshCw className="w-4 h-4 mr-2" />
                              Unlock Account
                            </DropdownMenuItem>
                          )}
                          
                          {/* Reset Password */}
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedUser(user);
                              setShowPasswordDialog(true);
                            }}
                            className="text-gray-300 hover:bg-gray-700 cursor-pointer"
                          >
                            <Key className="w-4 h-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>

                          <DropdownMenuSeparator className="bg-gray-700" />
                          
                          {/* Delete User - Disabled (use deactivate instead) */}
                          <DropdownMenuItem
                            disabled
                            className="text-gray-500 cursor-not-allowed opacity-50"
                            title="User deletion is disabled. Use 'Deactivate' to disable user access while preserving audit history."
                          >
                            <UserX className="w-4 h-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription className="text-gray-400">
              Add a new user to the system with specified role and permissions
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={newUserFirstName}
                  onChange={(e) => setNewUserFirstName(e.target.value)}
                  placeholder="John"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={newUserLastName}
                  onChange={(e) => setNewUserLastName(e.target.value)}
                  placeholder="Doe"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                placeholder="user@example.com"
                className="bg-gray-700 border-gray-600 text-white"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="username">Username *</Label>
              <Input
                id="username"
                type="text"
                value={newUserUsername}
                onChange={(e) => setNewUserUsername(e.target.value)}
                placeholder="johndoe"
                className="bg-gray-700 border-gray-600 text-white"
                required
              />
              <p className="text-xs text-gray-400 mt-1">
                Minimum 3 characters, maximum 100 characters
              </p>
            </div>
            
            <div>
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  placeholder="Enter password"
                  className="bg-gray-700 border-gray-600 text-white pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Minimum 8 characters, must include uppercase, lowercase, number and special character
              </p>
            </div>
            
            <div>
              <Label htmlFor="role">Role *</Label>
              <Select value={newUserRole} onValueChange={(value: any) => setNewUserRole(value)}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-700">
                  <SelectItem value="admin">Admin - Full system access</SelectItem>
                  <SelectItem value="team">Team - Internal operations</SelectItem>
                  <SelectItem value="external">External - Client access</SelectItem>
                  <SelectItem value="guest">Guest - Limited access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetCreateForm();
                }}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={() => createUserMutation.mutate()}
                disabled={!newUserEmail || !newUserUsername || !newUserPassword || createUserMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {createUserMutation.isPending ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription className="text-gray-400">
              Set a new password for {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-gray-700 border-gray-600 text-white pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-sm text-red-400">Passwords do not match</p>
            )}
            
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPasswordDialog(false);
                  setSelectedUser(null);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectedUser && newPassword === confirmPassword) {
                    resetPasswordMutation.mutate({ userId: selectedUser.id, password: newPassword });
                  }
                }}
                disabled={!newPassword || newPassword !== confirmPassword || resetPasswordMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {resetPasswordMutation.isPending ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription className="text-gray-400">
              Update user information, short code, and color
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editFirstName">First Name</Label>
                <Input
                  id="editFirstName"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  placeholder="John"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div>
                <Label htmlFor="editLastName">Last Name</Label>
                <Input
                  id="editLastName"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  placeholder="Doe"
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="editEmail">Email</Label>
              <Input
                id="editEmail"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="user@example.com"
                className="bg-gray-700 border-gray-600 text-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="editShortCode">Short Code (3 letters)</Label>
                <Input
                  id="editShortCode"
                  value={editShortCode}
                  onChange={(e) => setEditShortCode(e.target.value.toUpperCase())}
                  placeholder="SAM"
                  maxLength={3}
                  className="bg-gray-700 border-gray-600 text-white"
                />
                <p className="text-xs text-gray-400 mt-1">
                  3-letter code for artist identification
                </p>
              </div>
              <div>
                <Label htmlFor="editColorCode">Color Code</Label>
                <div className="flex gap-2">
                  <Input
                    id="editColorCode"
                    type="color"
                    value={editColorCode || '#6b7280'}
                    onChange={(e) => setEditColorCode(e.target.value)}
                    className="bg-gray-700 border-gray-600 h-10 w-16 cursor-pointer"
                  />
                  <Input
                    value={editColorCode || '#6b7280'}
                    onChange={(e) => setEditColorCode(e.target.value)}
                    placeholder="#6b7280"
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Color for artist badge display
                </p>
              </div>
            </div>
            
            {editShortCode && (
              <div className="p-3 bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">Preview:</p>
                <div 
                  className="inline-block px-2 py-1 rounded text-xs font-medium text-white"
                  style={{ backgroundColor: editColorCode || '#6b7280' }}
                >
                  {editShortCode}
                </div>
              </div>
            )}
            
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => {
                  setShowEditDialog(false);
                  setSelectedUser(null);
                }}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectedUser) {
                    editUserMutation.mutate({
                      userId: selectedUser.id,
                      data: {
                        email: editEmail,
                        firstName: editFirstName || null,
                        lastName: editLastName || null,
                        shortCode: editShortCode || null,
                        colorCode: editColorCode || null,
                      },
                    });
                  }
                }}
                disabled={!editEmail || editUserMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {editUserMutation.isPending ? 'Updating...' : 'Update User'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Picture Upload Dialog */}
      <ProfilePictureUploadDialog
        open={showProfilePictureDialog}
        onOpenChange={setShowProfilePictureDialog}
        userId={selectedUser ? parseInt(selectedUser.id) : 0}
        currentProfileImage={selectedUser?.profileImage}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['/api/auth/users'] });
          setShowProfilePictureDialog(false);
          setSelectedUser(null);
        }}
      />
    </div>
  );
}